import { useEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { useAuthState } from "react-firebase-hooks/auth";
import { CssBaseline, ThemeProvider } from "@mui/material/";
import createCache from "@emotion/cache";
import { useTranslation } from "react-i18next";
import stylisRTLPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";
import Clarity from "@microsoft/clarity";

import "./firebase/firebase";
import { auth, messaging } from "./firebase/firebase";
import { addAccessTokenInterceptor } from "./api/axios";
import getTheme from "./theme";
import LocalizationWrapper from "./providers/LocalizationWrapper";
import { requestPermission } from "./api/notifications";
import { isSupported, MessagePayload, onMessage } from "firebase/messaging";
import AppRouter from "./routers/AppRouter";
import appQueryClient from "./api/queries/queryClient";

function App() {
  const [user, isLoading] = useAuthState(auth);
  const [isTokenLoaded, setIsTokenLoaded] = useState<boolean>(false);
  const { i18n } = useTranslation();

  const cacheRtl = createCache({
    key: i18n.language === "ar" ? "rtl" : "ltr",
    stylisPlugins: i18n.language === "ar" ? [stylisRTLPlugin] : [],
  });

  useEffect(() => {
    if (!user) return;

    let unsubscribe: (() => void) | undefined;
    user.getIdTokenResult(true).then(async (results) => {
      addAccessTokenInterceptor(results.token);
      setIsTokenLoaded(true);
      await requestPermission();

      const supported = await isSupported().catch(() => false);
      if (!supported) {
        return;
      }

      // Listen for incoming Firebase push notifications
      unsubscribe = onMessage(messaging, (payload: MessagePayload) => {
        try {
          const notificationBody = payload?.notification?.body;
          if (!notificationBody) return;
          toast.info(notificationBody, { autoClose: 5000 });
        } catch (err) {
        } finally {
          appQueryClient.invalidateQueries({ queryKey: ["Notifications"] });
        }
      });
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development" && process.env.REACT_APP_CLARITY_PROJECT_ID) {
      Clarity.init(process.env.REACT_APP_CLARITY_PROJECT_ID);
    }
  }, []);

  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={getTheme(i18n.language === "ar" ? "rtl" : "ltr")}>
        <CssBaseline />
        <Router>
          <QueryClientProvider client={appQueryClient}>
            <LocalizationWrapper>
              <AppRouter user={user} isLoading={isLoading} isTokenLoaded={isTokenLoaded} />
              <ToastContainer
                position="bottom-left"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
              />
            </LocalizationWrapper>
          </QueryClientProvider>
        </Router>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
